import type { TestCaseStatus, TestStep, TestStepStatus } from '@/api/types'
import { localized } from '@/i18n/localized'

/** Un résultat d'étape tel qu'un lecteur le lit : campagne QA (#309) ou recette au portail (#315). */
export interface ScenarioStepResult {
  step_id: string
  status: TestStepStatus
  comment: string | null
}

/** Un cas tel qu'un lecteur le lit — `QaCase` et `PortalTestCase` le sont tous deux. */
export interface ScenarioCase {
  id: string
  title: string
  summary: string
  procedure: TestStep[]
  status: TestCaseStatus
  step_results: readonly ScenarioStepResult[]
  /** Réservé à l'équipe (lot 27) ; absent au portail. */
  internal?: boolean
}

/** Le filtre du lecteur, le même que celui de la page de campagne. */
export type QaFilter = 'tous' | 'a-faire' | 'problemes' | 'internes'

export const QA_FILTERS: readonly QaFilter[] = ['tous', 'a-faire', 'problemes', 'internes']

/** #335 : lus au portail dans la langue du contact. */
export const QA_FILTER_LABELS = localized<Record<QaFilter, string>>('QA_FILTER_LABELS', {
  fr: { tous: 'Tous les cas', 'a-faire': 'À tester', problemes: 'Problèmes', internes: 'Internes' },
  en: { tous: 'All cases', 'a-faire': 'To test', problemes: 'Problems', internes: 'Internal' },
})

export function isQaFilter(value: unknown): value is QaFilter {
  return typeof value === 'string' && (QA_FILTERS as readonly string[]).includes(value)
}

export function keepCase(testCase: ScenarioCase, filter: QaFilter): boolean {
  switch (filter) {
    case 'tous':
      return true
    case 'a-faire':
      return testCase.status === 'pending'
    case 'problemes':
      return testCase.status !== 'pending' && testCase.status !== 'passed'
    case 'internes':
      return testCase.internal === true
  }
}

/** Une diapositive : l'ouverture d'un cas, une de ses étapes, ou la fin. */
export type Slide<C extends ScenarioCase = ScenarioCase> =
  | { kind: 'case'; caseIndex: number; testCase: C }
  | {
      kind: 'step'
      caseIndex: number
      testCase: C
      step: TestStep
      stepIndex: number
      result: C['step_results'][number] | undefined
    }
  | { kind: 'end' }

/**
 * Le scénario à jouer (#309) : pour chaque cas retenu par le filtre, sa
 * diapositive d'ouverture puis une par étape ; une diapositive de fin.
 */
export function buildSlides<C extends ScenarioCase>(
  cases: readonly C[],
  filter: QaFilter,
): Slide<C>[] {
  const slides: Slide<C>[] = []
  cases
    .filter((testCase) => keepCase(testCase, filter))
    .forEach((testCase, caseIndex) => {
      slides.push({ kind: 'case', caseIndex, testCase })
      const results = new Map<string, C['step_results'][number]>(
        testCase.step_results.map((result) => [result.step_id, result]),
      )
      testCase.procedure.forEach((step, stepIndex) => {
        slides.push({
          kind: 'step',
          caseIndex,
          testCase,
          step,
          stepIndex,
          result: results.get(step.id),
        })
      })
    })
  slides.push({ kind: 'end' })
  return slides
}

/** Joué : un résultat autre que `pending`. */
export function played(slide: Slide): boolean {
  return slide.kind === 'step' && slide.result !== undefined && slide.result.status !== 'pending'
}

/**
 * Où ouvrir : la position de l'adresse (`cas`, `etape`, `fin`) si elle
 * existe encore, sinon la première étape à faire, sinon la fin.
 */
export function startIndex(slides: readonly Slide[], at: QaPosition): number {
  if (at.fin) return slides.length - 1
  if (at.cas) {
    const found = slides.findIndex((slide) =>
      at.etape
        ? slide.kind === 'step' && slide.testCase.id === at.cas && slide.step.id === at.etape
        : slide.kind === 'case' && slide.testCase.id === at.cas,
    )
    if (found !== -1) return found
  }
  const pending = slides.findIndex((slide) => slide.kind === 'step' && !played(slide))
  return pending === -1 ? slides.length - 1 : pending
}

/** Une position dans le scénario, telle que l'adresse la porte. */
export interface QaPosition {
  cas?: string
  etape?: string
  fin?: boolean
}

/** La position d'une diapositive, pour l'adresse : un rechargement la garde. */
export function positionOf(slide: Slide | undefined): QaPosition {
  if (!slide) return {}
  if (slide.kind === 'end') return { fin: true }
  if (slide.kind === 'case') return { cas: slide.testCase.id }
  return { cas: slide.testCase.id, etape: slide.step.id }
}

/** Les étapes en problème, dans l'ordre du scénario, pour la diapositive de fin. */
export function problemSteps<C extends ScenarioCase>(
  slides: readonly Slide<C>[],
): Extract<Slide<C>, { kind: 'step' }>[] {
  return slides.filter(
    (slide): slide is Extract<Slide<C>, { kind: 'step' }> =>
      slide.kind === 'step' &&
      slide.result !== undefined &&
      slide.result.status !== 'pending' &&
      slide.result.status !== 'passed',
  )
}
